import React, { useEffect, useRef, useState } from "react";
import { useIntl } from "react-intl";
import { useQuery } from "react-query";
import { makeStyles } from "tss-react/mui";
import { Box } from "@mui/material";

import { GenericObject } from "../../services/Price";
import { useDebounce } from "../../hooks/debounce";
import { getSelectFilterValues } from "../utils";
import { FilterSelect, Item, Option, OptionList } from "../interfaces";
import { CustomAutocomplete } from "../../components/Inputs/CustomAutocomplete";

const useStyles = makeStyles()(() => ({
  // Only the field's width: fill and outline come from the wrapper and the theme.
  field: {
    flex: "0 1 224px",
  },
}));

const EMPTY_PLACEHOLDER_ID = "<EMPTY>";
const EMPTY_PLACEHOLDER_LABEL = "EMPTY";

const useGetWatchLookupValue = (
  filters: GenericObject,
  filter: FilterSelect
) => {
  return filter.watchLookup ? filters[filter.watchLookup] : false;
};

export const SelectFilter = ({
  config,
  filters,
  filter,
  onChange,
  updateFilterOptions,
  filterValue,
  orderingFilterValue,
}: {
  config: {
    api: string;
    apiParams?: GenericObject;
    label: string;
    labelKey: string;
    type: string;
  };
  filterValue: Option | null;
  orderingFilterValue?: Option | null;
  filters: GenericObject;
  filter: FilterSelect;
  onChange: (newValue: any) => void;
  updateFilterOptions?: (options: Option) => void;
}) => {
  const intl = useIntl();
  const { classes: basicsClasses } = useStyles();
  const [options, setOptions] = useState<OptionList>([]);
  const [initialValues, setInitialValues] = useState<string | null>(null);
  const [newValue, setNewValue] = useState<Option[] | null>(null);
  const [configParams, setConfigParams] = useState<GenericObject | undefined>(
    () => {
      if (
        filterValue !== null &&
        filterValue !== undefined &&
        typeof filterValue !== "object"
      ) {
        return { ...config.apiParams, id: filterValue };
      }
      return config.apiParams;
    }
  );

  // Get value of watched lookup if exist
  const lookupValue: string | false = useGetWatchLookupValue(filters, filter);
  const debounceSearchValue = useDebounce(initialValues, 400);
  const watchLookupFirstRender = useRef(true);

  const query = useQuery([config.api, configParams, debounceSearchValue], () =>
    getSelectFilterValues<Item>(config.api, configParams, debounceSearchValue)
  );

  /**
   * Check if current select watch for change on other filter then set new configParam with watched lookup value
   */
  useEffect(() => {
    if (filter.watchLookup && lookupValue) {
      setConfigParams((prev) => {
        return {
          ...prev,
          property: lookupValue,
        };
      });
    }
  }, [lookupValue, filter.watchLookup]);

  useEffect(() => {
    if (watchLookupFirstRender.current) {
      watchLookupFirstRender.current = false;
      return;
    }
    if (filter.watchLookup) {
      // Reset filter input value if watched filter input change
      onChange("");
      setNewValue([]);
    }
  }, [onChange, filter, lookupValue]);

  useEffect(() => {
    if (query.data) {
      // The normal behavior of Select and Filter prevent selecting an empty
      // value when there is other options
      // So in case we want to filter on empty values we need to bypass this behavior
      // by adding a specific option for empty value
      setOptions(
        query.data.results.map((option: Item) => ({
          id:
            filter.allowEmpty && !option.id ? EMPTY_PLACEHOLDER_ID : option.id,
          label:
            filter.allowEmpty && !option.id
              ? EMPTY_PLACEHOLDER_LABEL
              : option[config.labelKey],
        }))
      );
    }
  }, [query.data, config.labelKey, filter.allowEmpty]);

  useEffect(() => {
    if (filter.usefulForRankability && options[0] && updateFilterOptions) {
      updateFilterOptions(options[0]);
    }
  }, [options, updateFilterOptions, filter.usefulForRankability]);

  useEffect(() => {
    setNewValue(orderingFilterValue ? [orderingFilterValue] : []);
  }, [orderingFilterValue]);

  // Reset filter on reset button filter click + hydratation depuis l'URL
  useEffect(() => {
    if (filterValue === null) {
      setNewValue(null);
      return;
    }
    if (
      filterValue !== undefined &&
      typeof filterValue !== "object" &&
      (newValue === null || newValue.length === 0) &&
      options.length > 0
    ) {
      const match = options.find((o) => String(o.id) === String(filterValue));
      if (match) {
        setNewValue([match]);
      }
    }
  }, [filterValue, options, newValue]);

  return (
    <Box className={basicsClasses.field}>
      <CustomAutocomplete
        disabled={!!filter.watchLookup && !lookupValue}
        filterOptions={(options) => options}
        formStyle={false}
        fullWidth
        getOptionLabel={(option) => {
          return option.label || "";
        }}
        isOptionEqualToValue={(option: Option, value: Option | null) => {
          return option.id === value?.id;
        }}
        loading={query.isLoading}
        options={options}
        onChange={(_, value) => {
          setNewValue(value ? [value] : []);
          onChange(value);
        }}
        onClick={(e) => {
          /* This one is to avoid the table re-rendering on the focus loss */
          e.stopPropagation();
        }}
        onInputChange={(event, newInputValue, reason) => {
          if (reason !== "reset") {
            setInitialValues(newInputValue);
          }
        }}
        placeholder={intl.formatMessage({ id: config.label })}
        variant="standard"
        renderOption={(props, option) => {
          return (
            <li {...props} key={option.id}>
              {filter.allowEmpty && option.id === EMPTY_PLACEHOLDER_ID ? (
                <i>{intl.formatMessage({ id: EMPTY_PLACEHOLDER_LABEL })}</i>
              ) : (
                <>{option.label}</>
              )}
            </li>
          );
        }}
        size="small"
        value={newValue && newValue.length > 0 ? newValue[0] : null}
      />
    </Box>
  );
};
